import { QueryHookOptions, useQuery } from '@apollo/client';
import { useContext, useMemo } from 'react';
import { Attachment } from 'sr-types/lib/search/v1/graphql';
import { UserContext } from '../../auth/UserContext';
import { FETCH_POLICY_CONFIG } from '../../constants/constants';
import { ENTITY_CONSTANTS } from '../../constants/entityConstants';
import { searchClient, slimSearchQuery } from '../../list/slimQuery';
import { ApprovalDataViewModel, DocumentSearchQueryData, WorkflowInfoQueryData } from '../types';
import {
    makeDocumentsSearchQueryVariables,
    transformDocumentsDataToApprovalAttachmentsData,
    transformWorkflowDataToApprovalData
} from './utils';
import { fetchWorkflowQuery, workflowGraphqlClient } from './workflowGraphqlApi';

/**
 * This hook fetches Approval data in two steps.
 * 1. It first fetches the workflow data associated with the approvalId (workflowId).
 * 2. It then fetches the attachments data for the document-references in the workflow, if any.
 *
 * It provides loading state, error state, and the transformed data ready for use in the UI
 * for both the approval and its attachments.
 */
export function useFetchApprovalApi(approvalId: string) {
    const { activeOrganizationAccount } = useContext(UserContext);

    const sharedQueryOptions: Partial<QueryHookOptions> = {
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        errorPolicy: 'all'
    };

    const workflowQueryOptions = {
        client: workflowGraphqlClient,
        variables: { id: approvalId },
        skip: !approvalId,
        ...sharedQueryOptions
    };

    const {
        data: workflowData,
        loading: isFetchingApprovalData,
        error: errorFetchingApprovalData,
        refetch: refetchApprovalData
    } = useQuery<WorkflowInfoQueryData>(fetchWorkflowQuery, workflowQueryOptions);

    const maybeAttachedDocuments = workflowData?.workflow?.context?.documentReferences;

    const documentsSearchQueryOptions = {
        client: searchClient,
        variables: makeDocumentsSearchQueryVariables(maybeAttachedDocuments ?? []),
        skip: !(maybeAttachedDocuments?.length > 0),
        ...sharedQueryOptions
    };

    const {
        data: approvalDocumentsData,
        loading: isFetchingApprovalAttachmentsData,
        error: errorFetchingApprovalAttachmentsData,
        refetch: refetchAttachmentsData
    } = useQuery<DocumentSearchQueryData>(
        slimSearchQuery(ENTITY_CONSTANTS.document, false),
        documentsSearchQueryOptions
    );

    const approvalAttachmentsData = useMemo(() => {
        const approvalAttachmentsData: Attachment[] =
            transformDocumentsDataToApprovalAttachmentsData(approvalDocumentsData);

        return approvalAttachmentsData;
    }, [approvalDocumentsData]);

    const approvalData = useMemo(() => {
        let approvalData: ApprovalDataViewModel | undefined = transformWorkflowDataToApprovalData(
            workflowData,
            approvalAttachmentsData
        );

        return approvalData;
    }, [approvalAttachmentsData, workflowData]);

    return {
        isFetchingApprovalData,
        errorFetchingApprovalData,
        approvalData,
        refetchApprovalData,
        isFetchingApprovalAttachmentsData,
        errorFetchingApprovalAttachmentsData,
        approvalAttachmentsData,
        refetchAttachmentsData
    };
}
